import { LayoutObjectType } from '@yard/app/api'
import { useMemo } from 'react'
import { Node } from 'reactflow'
import { LayoutObjectWithActions } from '../models/yard-view.model'
import { LayoutObjectViewStore } from '../stores/nodes/layout-object.view-store'
import { YardViewStore } from '../stores/yard.view-store'

const FOCUSED_Z_INDEX = 1000

export const useLayoutObjectNodes = (
  viewStore: YardViewStore,
  layoutObjectViewStore: LayoutObjectViewStore,
): Node<LayoutObjectWithActions>[] =>
  useMemo<Node<LayoutObjectWithActions>[]>(() => {
    const gates = viewStore.layoutObjects
      .filter(layoutObject => layoutObject.type === LayoutObjectType.Gate)
      .map<Node<LayoutObjectWithActions>>(block => ({
        id: block.id,
        type: 'gate',
        zIndex: -1,
        className: 'background-object',
        ...(viewStore.focusedLayoutObjectId === block.id && { zIndex: FOCUSED_Z_INDEX }),
        position: {
          x: block.layoutPosition.x,
          y: block.layoutPosition.y,
        },
        style: {
          width: block.width,
          height: block.height,
        },
        data: {
          ...block,
          isEditable: !viewStore.isViewMode,
          showNodeHeaders: viewStore.showNodeHeaders,
          focused: viewStore.focusedLayoutObjectId === block.id,
          onEdit: layoutObjectViewStore.openGateFormDialog,
          onFocus: viewStore.setFocusedLayoutObjectId,
          onDelete: layoutObjectViewStore.setDeleteDialogOpen,
          onResize: layoutObjectViewStore.setSize,
          onRotate: layoutObjectViewStore.setRotation,
        },
        draggable: !viewStore.isViewMode,
        selectable: !viewStore.isViewMode,
        connectable: !viewStore.isViewMode,
      }))

    const makeLayoutObjectNodes = (
      layoutType: LayoutObjectType,
      onEdit: (id?: string) => void,
    ): Node<LayoutObjectWithActions>[] =>
      viewStore.layoutObjects
        .filter(layoutObject => layoutObject.type === layoutType)
        .map<Node<LayoutObjectWithActions>>(block => ({
          id: block.id,
          type: 'layoutObject',
          zIndex: -1,
          className: 'background-object',
          ...(viewStore.focusedLayoutObjectId === block.id && { zIndex: FOCUSED_Z_INDEX }),
          position: {
            x: block.layoutPosition.x,
            y: block.layoutPosition.y,
          },
          style: {
            width: block.width,
            height: block.height,
          },
          data: {
            ...block,
            isEditable: !viewStore.isViewMode,
            showNodeHeaders: viewStore.showNodeHeaders,
            focused: viewStore.focusedLayoutObjectId === block.id,
            onEdit,
            onFocus: viewStore.setFocusedLayoutObjectId,
            onDelete: layoutObjectViewStore.setDeleteDialogOpen,
            onResize: layoutObjectViewStore.setSize,
            onRotate: layoutObjectViewStore.setRotation,
          },
          draggable: !viewStore.isViewMode,
          selectable: !viewStore.isViewMode,
          connectable: !viewStore.isViewMode,
        }))

    const holdingPoints = makeLayoutObjectNodes(
      LayoutObjectType.HoldingPoint,
      layoutObjectViewStore.openHoldingPointFormDialog,
    )

    const handoverAreas = makeLayoutObjectNodes(
      LayoutObjectType.HandoverArea,
      layoutObjectViewStore.openHandoverAreaFormDialog,
    )

    const nonCanvasNodeTypes = new Set<LayoutObjectType>([
      LayoutObjectType.Gate,
      LayoutObjectType.HoldingPoint,
      LayoutObjectType.HandoverArea,
    ])

    const foreground = viewStore.layoutObjects
      .filter(layoutObject => !layoutObject.imagePath && !nonCanvasNodeTypes.has(layoutObject.type))
      .map<Node<LayoutObjectWithActions>>(block => ({
        id: block.id,
        type: 'layoutObject',
        zIndex: -1,
        className: `background-object`,
        ...(viewStore.focusedLayoutObjectId === block.id && { zIndex: FOCUSED_Z_INDEX }),
        position: {
          x: block.layoutPosition.x,
          y: block.layoutPosition.y,
        },
        style: {
          width: block.width,
          height: block.height,
        },
        data: {
          ...block,
          isEditable: !viewStore.isViewMode,
          showNodeHeaders: viewStore.showNodeHeaders,
          focused: viewStore.focusedLayoutObjectId === block.id,
          onEdit: layoutObjectViewStore.openFormDialog,
          onFocus: viewStore.setFocusedLayoutObjectId,
          onDelete: layoutObjectViewStore.setDeleteDialogOpen,
          onResize: layoutObjectViewStore.setSize,
          onRotate: layoutObjectViewStore.setRotation,
        },
        draggable: !viewStore.isViewMode,
        selectable: !viewStore.isViewMode,
        connectable: !viewStore.isViewMode,
      }))

    const background = viewStore.layoutObjects
      .filter(layoutObject => layoutObject.imagePath && !nonCanvasNodeTypes.has(layoutObject.type))
      .map<Node<LayoutObjectWithActions>>(block => ({
        id: block.id,
        type: 'layoutObject',
        zIndex: -2,
        className: `background-node${viewStore.backgroundMode === 'image' ? '' : ' background-hidden'}`,
        position: {
          x: block.layoutPosition.x,
          y: block.layoutPosition.y,
        },
        style: {
          width: block.width,
          height: block.height,
        },
        data: {
          ...block,
          isEditable: !viewStore.isViewMode && !viewStore.isBackgroundEditBlocked,
          showNodeHeaders: viewStore.showNodeHeaders,
          focused: viewStore.focusedLayoutObjectId === block.id,
          onEdit: layoutObjectViewStore.openBackgroundLayoutObjectFormDialog,
          onFocus: viewStore.setFocusedLayoutObjectId,
          onDelete: layoutObjectViewStore.setDeleteDialogOpen,
          onResize: layoutObjectViewStore.setSize,
          onRotate: layoutObjectViewStore.setRotation,
        },
        draggable: !viewStore.isViewMode && !viewStore.isBackgroundEditBlocked,
        selectable: !viewStore.isViewMode && !viewStore.isBackgroundEditBlocked,
        connectable: !viewStore.isViewMode && !viewStore.isBackgroundEditBlocked,
      }))

    return [...gates, ...holdingPoints, ...handoverAreas, ...foreground, ...background]
  }, [
    viewStore.layoutObjects,
    viewStore.focusedLayoutObjectId,
    viewStore.isViewMode,
    viewStore.isBackgroundEditBlocked,
    viewStore.backgroundMode,
    viewStore.showNodeHeaders,
    viewStore.setFocusedLayoutObjectId,
    layoutObjectViewStore,
  ])
